/**
 * RIM — RMD Withdrawal Flow (4-step wizard).
 *
 * Port target:
 *   - Legacy SPFx `IraManagementScreen.tsx` RMD section + `RmdPanel.tsx`
 *     (Uniform Lifetime table + SECURE Act 2.0 cohorts).
 *
 * Steps:
 *   1. Age gate (config-driven — `IraConfig` rows `rmd-start-age` +
 *      `rmd-factor`). NEVER hardcoded 70½/73/75.
 *   2. Mode — full (equals calculated) or partial (lesser amount).
 *   3. Tax withholding (fed/state %).
 *   4. Review → POST /v3/ira/rmd-payments (Wave 5.2 IRA-FN-RMD).
 *
 * Server contract:
 *   - The Function recomputes the RMD from priorYearEndBalance + the
 *     IraRmdFactor row. Officer's local calc must reconcile within $0.50.
 *     Mismatch → 422 RMD_AMOUNT_MISMATCH (we render side-by-side).
 *   - SECURE 2.0 cohort start age resolved server-side (admin-config'd via
 *     SystemSettings; defaults per port-plan §7.4). Below cohort age →
 *     409 RMD_NOT_DUE with cohort explanation cited inline.
 *   - Missing IraRmdFactor row → 422 RMD_FACTOR_NOT_SEEDED with admin link.
 */
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { ArrowLeft, CheckCircle2 } from '@retail-ops/panel-shell/fi';
import { Button, KVPair, Pill, Stepper, WidgetCard } from '@/components/primitives';
import { EmptyState } from '@/components/common/EmptyState';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { useConfig } from '@/contexts/ConfigContext';
import { useAuth } from '@/contexts/AuthContext';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useIraDetail } from '../hooks/useIraDetail';
import { useEmitEvent } from '../hooks/useEmitEvent';
import type {
  IraRmdPaymentRequest,
  IraRmdResponse,
  IraRmdMode,
  IraRmdDelivery,
  IraRmdCohortExplanation,
} from '@retail-ops/shared-types';
import '../styles/rim.css';

const STEPS = [
  { label: 'Age gate' },
  { label: 'Mode' },
  { label: 'Tax withholding' },
  { label: 'Review & submit' },
];

interface RmdDraft {
  rmdYear: number;
  mode: 'full' | 'partial';
  partialAmount: string;
  fedPct: string;
  statePct: string;
}

function ageFromIso(iso: string | null): number | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const ms = Date.now() - d.getTime();
  return ms / (365.25 * 24 * 3600 * 1000);
}

export function RmdWithdrawalFlow(): JSX.Element {
  const { planNumber } = useParams<{ planNumber: string }>();
  const navigate = useNavigate();
  const { config } = useConfig();
  const { user } = useAuth();
  const client = useApiClient();
  const emit = useEmitEvent();

  const { data: ira, loading: loadingIra, error: iraError } = useIraDetail(planNumber);

  const currentYear = new Date().getFullYear();
  const [step, setStep] = useState<number>(0);
  const [draft, setDraft] = useState<RmdDraft>({
    rmdYear: currentYear,
    mode: 'full',
    partialAmount: '',
    fedPct: '10',
    statePct: '0',
  });
  const [submitState, setSubmitState] = useState<'idle' | 'submitting' | 'submitted' | 'error'>('idle');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitErrorCode, setSubmitErrorCode] = useState<string | null>(null);
  const [serverCalcAmount, setServerCalcAmount] = useState<number | null>(null);
  const [officerCalcEcho, setOfficerCalcEcho] = useState<number | null>(null);
  const [cohortInfo, setCohortInfo] = useState<IraRmdCohortExplanation | null>(null);
  const [postedSummary, setPostedSummary] = useState<{
    serverCalcAmount: number;
    distributionAmount: number;
    remainingRmd: number | null;
    cohortLabel: string;
    transactionId: string;
    transactionCode: string;
    f803Stamped: boolean;
  } | null>(null);

  // RMD start age — entirely config-driven (cohort aware).
  const rmdStartAge = useMemo<number | null>(() => {
    const rows = (config.iraConfig ?? []).filter((r) => r.configType === 'rmd-start-age' && r.rmdStartAge);
    if (rows.length === 0) return null;
    const birthYear = ira?.ownerBirthDate ? new Date(ira.ownerBirthDate).getFullYear() : null;
    if (birthYear != null) {
      const match = rows.find((r) => {
        const c = r.rmdStartAge;
        if (!c) return false;
        const from = c.birthYearFrom ?? -Infinity;
        const to = c.birthYearTo ?? Infinity;
        return birthYear >= from && birthYear <= to;
      });
      if (match?.rmdStartAge) return match.rmdStartAge.startAge;
    }
    return rows[0]?.rmdStartAge?.startAge ?? null;
  }, [config.iraConfig, ira?.ownerBirthDate]);

  // RMD factor (distribution period) keyed by integer age.
  const rmdFactorByAge = useMemo<Map<number, number>>(() => {
    const m = new Map<number, number>();
    (config.iraConfig ?? []).forEach((r) => {
      if (r.configType === 'rmd-factor' && r.rmdFactor) {
        m.set(r.rmdFactor.age, r.rmdFactor.distributionPeriod);
      }
    });
    return m;
  }, [config.iraConfig]);

  const ownerAge = ageFromIso(ira?.ownerBirthDate ?? null);
  const ageFloor = ownerAge !== null ? Math.floor(ownerAge) : null;
  const factor = ageFloor !== null ? (rmdFactorByAge.get(ageFloor) ?? null) : null;

  const gateMet = ownerAge !== null && rmdStartAge !== null && ownerAge >= rmdStartAge;
  const calcAmount = useMemo<number | null>(() => {
    if (!ira || factor == null || ira.priorYearEndBalance == null) return null;
    return ira.priorYearEndBalance / factor;
  }, [ira, factor]);

  const distributionAmount = draft.mode === 'full'
    ? calcAmount ?? 0
    : Number.parseFloat(draft.partialAmount || '0');
  const fedNum = Number.parseFloat(draft.fedPct);
  const stateNum = Number.parseFloat(draft.statePct);

  if (!planNumber) return <EmptyState title="Plan number required" message="Open an IRA plan from RIM before starting an RMD withdrawal." />;
  if (loadingIra) return <div className="rim-flow__loading"><LoadingSpinner label="Loading IRA plan…" size="lg" /></div>;
  if (iraError || !ira) {
    return (
      <EmptyState
        title="Unable to load IRA plan"
        message={iraError ?? 'Core Director did not return plan details.'}
        action={<Button variant="ghost" startIcon={<ArrowLeft size={14} />} onClick={() => navigate('..')}>Back to plan</Button>}
      />
    );
  }

  const canProceed = ((): boolean => {
    switch (step) {
      case 0: return gateMet && calcAmount !== null;
      case 1: return draft.mode === 'full' || (Number.isFinite(distributionAmount) && distributionAmount > 0 && calcAmount != null && distributionAmount <= calcAmount);
      case 2: return Number.isFinite(fedNum) && fedNum >= 0 && fedNum <= 100 && Number.isFinite(stateNum) && stateNum >= 0 && stateNum <= 100;
      case 3: return submitState === 'idle' || submitState === 'error';
      default: return false;
    }
  })();

  const submit = async (): Promise<void> => {
    if (calcAmount == null) {
      setSubmitError('Calculated RMD amount unavailable — cannot post.');
      setSubmitErrorCode('INVALID_AMOUNT');
      setSubmitState('error');
      return;
    }

    const officerCode = user?.officerCode ?? user?.upn;
    if (!officerCode) {
      setSubmitError('Missing officer identity (officerCode/upn). Sign in again.');
      setSubmitErrorCode('MISSING_OFFICER');
      setSubmitState('error');
      return;
    }

    setSubmitState('submitting');
    setSubmitError(null);
    setSubmitErrorCode(null);
    setServerCalcAmount(null);
    setOfficerCalcEcho(null);
    setCohortInfo(null);

    const mode: IraRmdMode = draft.mode === 'full' ? 'FULL_RMD' : 'PARTIAL';
    const delivery: IraRmdDelivery = 'CHECK_TO_OWNER';

    const reqBody: IraRmdPaymentRequest = {
      planNumber: ira.planNumber,
      rmdYear: draft.rmdYear,
      mode,
      calcAmount,
      distributionAmount,
      fedWithholdingPct: fedNum,
      stateWithholdingPct: stateNum,
      delivery,
      officerCode,
    };

    try {
      const resp: IraRmdResponse = await client.retail.ira.postRmd(reqBody);

      if (resp.success) {
        const data = resp.data;
        setPostedSummary({
          serverCalcAmount: data.serverCalcAmount,
          distributionAmount: data.distributionAmount,
          remainingRmd: data.remainingRmd,
          cohortLabel: data.cohortLabel,
          transactionId: data.transactionId,
          transactionCode: data.transactionCode,
          f803Stamped: data.f803RmdFormOnFileStamped,
        });
        emit({
          eventType: 'ira-rmd-posted',
          eventCategory: 'cif-account',
          severity: 'warn',
          context: `plan:${ira.planNumber}`,
          payload: {
            planNumber: ira.planNumber,
            rmdYear: draft.rmdYear,
            mode,
            distributionAmount: data.distributionAmount,
            serverCalcAmount: data.serverCalcAmount,
            remainingRmd: data.remainingRmd,
            transactionId: data.transactionId,
            cohortLabel: data.cohortLabel,
          },
        });
        setSubmitState('submitted');
        return;
      }

      // Failure envelope — render code-specific guidance
      setSubmitErrorCode(resp.code);
      setSubmitError(resp.error);
      if (resp.code === 'RMD_AMOUNT_MISMATCH') {
        if (typeof resp.serverCalcAmount === 'number') setServerCalcAmount(resp.serverCalcAmount);
        if (typeof resp.officerCalcAmount === 'number') setOfficerCalcEcho(resp.officerCalcAmount);
      }
      if (resp.code === 'RMD_NOT_DUE' && resp.cohort) {
        setCohortInfo(resp.cohort);
      }
      setSubmitState('error');
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'POST /v3/ira/rmd-payments failed.');
      setSubmitErrorCode('POST_FAILED');
      setSubmitState('error');
    }
  };

  return (
    <div className="rim-flow">
      <div className="rim-flow__crumb">
        <Button variant="ghost" startIcon={<ArrowLeft size={14} />} onClick={() => navigate('..')}>Back to plan</Button>
        <h1>RMD withdrawal · {ira.planDescription ?? ira.planCodeRaw} · {ira.planNumber}</h1>
      </div>

      <Stepper steps={STEPS} currentIndex={step} variant="apply" />

      {step === 0 && (
        <WidgetCard title="Age gate (config-driven)" stripe="compliance">
          <p className="rim-flow__helper">
            RMD start age and life-expectancy factors come from <code>config.iraConfig</code> rows
            (<code>rmd-start-age</code>, <code>rmd-factor</code>). Nothing is hardcoded — if the
            ETL hasn&apos;t seeded these rows, the UI renders &ldquo;—&rdquo; and the flow is blocked.
          </p>
          <div className="rim-kv-grid">
            <KVPair label="Plan type" value={ira.planCode} />
            <KVPair label="Owner age (today)" value={ownerAge !== null ? ownerAge.toFixed(1) : '—'} />
            <KVPair label="RMD start age (config)" value={rmdStartAge ?? '—'} />
            <KVPair label="Gate" value={gateMet ? <Pill variant="warn">Required</Pill> : <Pill variant="info">Not yet required</Pill>} />
            <KVPair label="Prior YE balance (Core)" value={ira.priorYearEndBalance != null ? `$${ira.priorYearEndBalance.toFixed(2)}` : '—'} />
            <KVPair label="Life expectancy factor (config)" value={factor !== null ? factor.toFixed(2) : '—'} />
            <KVPair label="Calculated RMD" value={calcAmount !== null ? `$${calcAmount.toFixed(2)}` : '—'} />
          </div>
          {!gateMet && (
            <div className="rim-rmd-banner">
              Owner does not meet the RMD age threshold (per seeded config). Use the Distribution flow for
              non-RMD withdrawals.
            </div>
          )}
          {gateMet && calcAmount === null && (
            <div className="rim-rmd-banner is-warn" role="alert">
              RMD factor for age {ageFloor} is missing from <code>IraConfig</code> — seed the current year&apos;s
              Uniform Lifetime table before distributing.
            </div>
          )}
        </WidgetCard>
      )}

      {step === 1 && (
        <WidgetCard title="Mode (full vs partial)" stripe="compliance">
          <div className="rim-flow__chip-row">
            <Button variant={draft.mode === 'full' ? 'primary' : 'ghost'} onClick={() => setDraft({ ...draft, mode: 'full' })}>
              Full RMD · ${calcAmount?.toFixed(2) ?? '—'}
            </Button>
            <Button variant={draft.mode === 'partial' ? 'primary' : 'ghost'} onClick={() => setDraft({ ...draft, mode: 'partial' })}>
              Partial (lesser amount)
            </Button>
          </div>
          {draft.mode === 'partial' && (
            <label className="rim-flow__field">
              <span className="rim-flow__field-label">Partial amount (USD)</span>
              <input
                type="number"
                className="rim-benef-field"
                value={draft.partialAmount}
                min={0}
                step={0.01}
                onChange={(e) => setDraft({ ...draft, partialAmount: e.currentTarget.value })}
              />
              <span className="rim-flow__hint">
                Must be ≤ calculated RMD (${calcAmount?.toFixed(2) ?? '—'}). Remaining balance will carry forward
                and owe 25% IRS shortfall penalty if not satisfied by 12/31.
              </span>
            </label>
          )}
        </WidgetCard>
      )}

      {step === 2 && (
        <WidgetCard title="Tax withholding" stripe="compliance">
          <div className="rim-flow__grid-2">
            <label className="rim-flow__field">
              <span className="rim-flow__field-label">Federal %</span>
              <input
                type="number"
                className="rim-benef-field"
                value={draft.fedPct}
                min={0}
                max={100}
                step={0.5}
                onChange={(e) => setDraft({ ...draft, fedPct: e.currentTarget.value })}
              />
            </label>
            <label className="rim-flow__field">
              <span className="rim-flow__field-label">State %</span>
              <input
                type="number"
                className="rim-benef-field"
                value={draft.statePct}
                min={0}
                max={100}
                step={0.5}
                onChange={(e) => setDraft({ ...draft, statePct: e.currentTarget.value })}
              />
            </label>
          </div>
        </WidgetCard>
      )}

      {step === 3 && (
        <WidgetCard title="Review & submit" stripe="compliance">
          <div className="rim-kv-grid">
            <KVPair label="Plan" value={`${ira.planCodeRaw} · ${ira.planNumber}`} />
            <KVPair label="CIF" value={ira.cifId ?? '—'} />
            <KVPair label="RMD year" value={draft.rmdYear} />
            <KVPair label="Mode" value={draft.mode === 'full' ? 'FULL_RMD' : 'PARTIAL'} />
            <KVPair
              label="Officer's calculated RMD"
              value={
                <span className="rim-rmd-calc-match">
                  ${(calcAmount ?? 0).toFixed(2)}
                  {serverCalcAmount !== null
                    && Math.abs(serverCalcAmount - (calcAmount ?? 0)) <= 0.5 && (
                      <CheckCircle2 size={14} color="var(--ok-text)" aria-label="Matches server" />
                    )}
                </span>
              }
            />
            {(serverCalcAmount !== null
              || (postedSummary?.serverCalcAmount !== undefined)) && (
              <KVPair
                label="Server-recomputed RMD"
                value={`$${(postedSummary?.serverCalcAmount ?? serverCalcAmount ?? 0).toFixed(2)}`}
              />
            )}
            <KVPair label="Distribution amount" value={`$${distributionAmount.toFixed(2)}`} />
            <KVPair label="Delivery" value="CHECK_TO_OWNER" />
            <KVPair label="Fed / State w/h" value={`${fedNum.toFixed(1)}% / ${stateNum.toFixed(1)}%`} />
          </div>

          {submitState === 'error' && submitErrorCode === 'RMD_AMOUNT_MISMATCH' && (
            <div className="rim-rmd-banner is-warn" role="alert">
              <strong>RMD amount mismatch.</strong> The server-recomputed RMD is{' '}
              <strong>${(serverCalcAmount ?? 0).toFixed(2)}</strong>; you submitted{' '}
              <strong>${(officerCalcEcho ?? calcAmount ?? 0).toFixed(2)}</strong>. The two must reconcile within $0.50.
              Refresh the plan to pick up the latest factor / prior-year-end balance and resubmit.
            </div>
          )}

          {submitState === 'error' && submitErrorCode === 'RMD_NOT_DUE' && cohortInfo && (
            <div className="rim-rmd-banner" role="alert">
              <strong>RMD not yet required.</strong>{' '}
              {cohortInfo.ownerBirthYear !== null && (
                <>Owner was born in <strong>{cohortInfo.ownerBirthYear}</strong>, which falls in the{' '}
                <strong>{cohortInfo.cohortLabel}</strong> SECURE 2.0 cohort (start age{' '}
                {cohortInfo.cohortStartAge === 70.5 ? '70½' : cohortInfo.cohortStartAge}).</>
              )}{' '}
              {cohortInfo.friendlyMessage}
            </div>
          )}

          {submitState === 'error' && submitErrorCode === 'RMD_FACTOR_NOT_SEEDED' && (
            <div className="rim-rmd-banner is-warn" role="alert">
              <strong>RMD factor missing for {draft.rmdYear}.</strong> An admin must seed the
              IRS Uniform Lifetime row for this owner's age before the post can succeed.
              Open <a href="/admin/ira/rmd-factors">Admin → IRA → RMD Factors</a> to add the row.
            </div>
          )}

          {submitState === 'error'
            && submitErrorCode !== 'RMD_AMOUNT_MISMATCH'
            && submitErrorCode !== 'RMD_NOT_DUE'
            && submitErrorCode !== 'RMD_FACTOR_NOT_SEEDED'
            && submitError && (
              <div className="rim-rmd-banner is-warn" role="alert">
                <strong>{submitErrorCode ?? 'POST_FAILED'}</strong> — {submitError}
              </div>
            )}

          {submitState === 'submitted' && postedSummary && (
            <div className="rim-rmd-banner" role="status">
              RMD posted (txn <code>{postedSummary.transactionId}</code>, TC{' '}
              <code>{postedSummary.transactionCode}</code>). Server confirmed{' '}
              <strong>${postedSummary.serverCalcAmount.toFixed(2)}</strong> RMD,{' '}
              distributed <strong>${postedSummary.distributionAmount.toFixed(2)}</strong>.
              {postedSummary.remainingRmd !== null && postedSummary.remainingRmd > 0 && (
                <> Remaining obligation: <strong>${postedSummary.remainingRmd.toFixed(2)}</strong> by year-end.</>
              )}
              {postedSummary.f803Stamped && <> F803 RMD-form-on-file flag updated.</>}
            </div>
          )}
        </WidgetCard>
      )}

      <div className="rim-flow__actions">
        {step > 0 && submitState !== 'submitted' && (
          <Button variant="ghost" onClick={() => setStep(step - 1)}>Back</Button>
        )}
        {step < STEPS.length - 1 && (
          <Button variant="primary" disabled={!canProceed} onClick={() => setStep(step + 1)}>Next</Button>
        )}
        {step === STEPS.length - 1 && submitState !== 'submitted' && (
          <Button variant="primary" disabled={submitState === 'submitting' || !canProceed} onClick={() => { void submit(); }}>
            {submitState === 'submitting' ? 'Posting…' : 'Submit RMD'}
          </Button>
        )}
        {submitState === 'submitted' && (
          <Button variant="primary" onClick={() => navigate('..')}>Done</Button>
        )}
      </div>
    </div>
  );
}
